import React, { useMemo, useEffect, useState, useCallback } from "react";
import { isEmpty } from "lodash";
import fp from "lodash/fp";
import { Col, Row } from "react-bootstrap";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { Field, change, formValueSelector } from "redux-form";

import { ViewSplitProfile } from "components";
import { PROCESSOR_SPONSOR_SECTION_NAME } from "globalConstants";
import {
	canViewResiduals,
	dispatchChangeScheduleAFirstAsDefault,
} from "helpers";
import { appStatuses, filterScheduleAsAccordingProcessor } from "mappers";
import { dropdownValuesService, agentSetupService } from "services";
import { PartnerTypes } from "types";

import { required } from "../../validators/validation";
import { CustomDropdownById } from "../formFields/dropdown";
import { ScheduleAView } from "../pages/scheduleA/scheduleAView";

import { ReactComponent as EYE } from "static/images/eye.svg";
import { ReactComponent as EYEHIDE } from "static/images/eye_hide.svg";
import styles from "./formSections.module.scss";

type ApplicationResidualsFieldsProps = {
	formName: string;
	sectionName: string;
	primaryAgents: Array<{
		value: string;
		name: string;
		partnerType: number;
		companyId: string;
	}>;
	applicationStatus: number;
	isUnderwriting?: boolean;
	scheduleAList?: { [key: string]: any };
	isLoadingScheduleAList?: boolean;
	initialValues: { [key: string]: any };
	handleGetScheduleAList: Function;
	setFilteredScheduleAs: Function;
	setRawScheduleAs: Function;
};

const ResidualsFields = ({
	formName,
	sectionName = "residualsSection",
	primaryAgents,
	applicationStatus,
	isUnderwriting,
	scheduleAList,
	isLoadingScheduleAList,
	initialValues,
	handleGetScheduleAList,
	setFilteredScheduleAs,
	setRawScheduleAs,
}: ApplicationResidualsFieldsProps) => {
	const dispatch = useDispatch();

	// only for Edit pages: set initial items for scheduleA ddl
	// run request one time (on page load) if exist primaryAgentId
	// don`t add other logic to this useEffect
	useEffect(() => {
		let scheduleAListData = [];
		const processorId =
			initialValues?.processorSponsorSection?.processor?.id;
		const primaryAgentId = initialValues?.primaryAgentId;
		const scheduleAId = initialValues?.residualsSection?.scheduleA?.id;
		const isEmptyScheduleAId = fp.isEmpty(scheduleAId);

		const setInitialValueForScheduleAList = async () => {
			scheduleAListData = await handleGetScheduleAList(primaryAgentId);
			setRawScheduleAs(scheduleAListData);
			if (processorId) {
				const filteredScheduleAsAccordingProcessor =
					filterScheduleAsAccordingProcessor?.(
						scheduleAListData,
						processorId,
					);
				setFilteredScheduleAs(filteredScheduleAsAccordingProcessor);

				// set default or leave initial value for ScheduleA DDL
				if (
					isEmptyScheduleAId ||
					// for case when previously was selected Available item which become UnAvailable
					filteredScheduleAsAccordingProcessor?.length === 1
				) {
					dispatchChangeScheduleAFirstAsDefault(
						dispatch,
						formName,
						filteredScheduleAsAccordingProcessor,
					);
				}
			}
		};

		if (primaryAgentId) {
			setInitialValueForScheduleAList();
		}
	}, [
		dispatch,
		formName,
		handleGetScheduleAList,
		initialValues?.primaryAgentId,
		initialValues?.processorSponsorSection?.processor?.id,
		initialValues?.residualsSection?.scheduleA?.id,
		setFilteredScheduleAs,
		setRawScheduleAs,
	]);

	const [showScheduleAPreview, setShowScheduleAPreview] = useState(false);
	const [showSplitProfilePreview, setShowSplitProfilePreview] =
		useState(false);

	const [
		isOnlyHighRiskSplitProfileAvailable,
		setIsOnlyHighRiskSplitProfileAvailable,
	] = useState(false);

	const selector = formValueSelector(formName);

	const getPrimaryAgentId = state => selector(state, "primaryAgentId");
	const getProcessorId = state =>
		selector(state, `${PROCESSOR_SPONSOR_SECTION_NAME}.processor.id`);

	const primaryAgentId = useSelector(getPrimaryAgentId);
	const processorId = useSelector(getProcessorId);

	const isPrimaryAgentIdEmpty = fp.isEmpty(primaryAgentId);
	const isProcessorIdEmpty =
		fp.isEmpty(processorId) && !fp.isNumber(processorId);

	const isEmptyPrimaryAgentIdProcessorId =
		!isPrimaryAgentIdEmpty && !isProcessorIdEmpty ? false : true;

	const getSplitProfileId = state =>
		selector(state, `${sectionName}.splitProfile.id`);
	const splitProfileId = useSelector(getSplitProfileId);

	const getScheduleAId = state =>
		selector(state, `${sectionName}.scheduleA.id`);
	const scheduleAId = useSelector(getScheduleAId);

	const selectedAgent = useMemo(
		() => primaryAgents?.find(agent => agent.value === primaryAgentId),
		[primaryAgentId, primaryAgents],
	);

	const { data: splitProfiles, isLoading: isLoadingSplitProfiles } = useQuery(
		["splitProfile", primaryAgentId, isEmptyPrimaryAgentIdProcessorId],
		() =>
			dropdownValuesService.getSplitProfiles(primaryAgentId, {
				showHighRisk: true,
			}),
		{
			enabled: !isEmptyPrimaryAgentIdProcessorId,
		},
	);

	const noHighRiskSplitProfiles = useMemo(() => {
		const applicationStatusToHideNoHighRiskSplitProfiles = [
			appStatuses.UNSIGNED_CREATED_BY_MERCHANT,
			appStatuses.REMOVED,
			appStatuses.UNSIGNED_PENDING_COMPLETION,
			appStatuses.UNSIGNED_PRE_APP_LINK_SENT,
			appStatuses.UPLOADED,
			appStatuses.UNSIGNED_REQUESTING_SIGN,
			appStatuses.UNSIGNED_SIGN_REQUESTED,
			appStatuses.UNSIGNED_SIGN_FAILED,
		];

		if (
			applicationStatusToHideNoHighRiskSplitProfiles.includes(
				applicationStatus,
			) ||
			!applicationStatus
		) {
			const listOfNoHighRiskSplitProfiles = splitProfiles?.filter(
				item => !item.isHighRisk,
			);

			setIsOnlyHighRiskSplitProfileAvailable(
				splitProfiles?.length && !listOfNoHighRiskSplitProfiles?.length
					? true
					: false,
			);

			return listOfNoHighRiskSplitProfiles;
		}

		setIsOnlyHighRiskSplitProfileAvailable(false);

		return splitProfiles;
	}, [splitProfiles, applicationStatus]);

	const isInsideSalesHouseAccountType = useMemo(
		() =>
			selectedAgent?.partnerType ===
				PartnerTypes.InsideSalesAgent.value ||
			selectedAgent?.partnerType ===
				PartnerTypes.InsideSalesManager.value ||
			selectedAgent?.partnerType ===
				PartnerTypes.AuroraHouseAccount.value,
		[selectedAgent?.partnerType],
	);

	const handleOnSplitProfileIconClick = () => {
		setShowSplitProfilePreview(!showSplitProfilePreview);
	};
	const handleOnScheduleAIconClick = () => {
		setShowScheduleAPreview(!showScheduleAPreview);
	};

	useEffect(() => {
		if (primaryAgentId && processorId) {
			setShowSplitProfilePreview(false);
			setShowScheduleAPreview(false);
		}
	}, [primaryAgentId, processorId]);

	// Set full scheduleA data into form filed to show on App Summary page
	useEffect(() => {
		if (scheduleAList && scheduleAList?.length > 0 && scheduleAId) {
			dispatch(
				change(
					formName,
					`${sectionName}.scheduleA`,
					scheduleAList.find(
						schedule => schedule.id === scheduleAId,
					) || { id: "" },
				),
			);
		}
	}, [dispatch, formName, scheduleAId, scheduleAList, sectionName]);

	// Set full splitProfile data into form filed to show on App Summary page
	useEffect(() => {
		if (splitProfiles && splitProfiles?.length > 0 && splitProfileId) {
			dispatch(
				change(
					formName,
					`${sectionName}.splitProfile`,
					splitProfiles.find(
						splitProfile => splitProfile.id === splitProfileId,
					) || { id: "" },
				),
			);
		}
	}, [dispatch, formName, splitProfileId, sectionName, splitProfiles]);

	const canViewResidualsDetails = useMemo(() => {
		const companyId = selectedAgent?.companyId;
		return canViewResiduals(primaryAgentId, companyId);
	}, [primaryAgentId, selectedAgent?.companyId]);

	const isRenderScheduleAPreview = useMemo(
		() =>
			primaryAgentId &&
			scheduleAId &&
			canViewResidualsDetails &&
			showScheduleAPreview,
		[
			canViewResidualsDetails,
			primaryAgentId,
			showScheduleAPreview,
			scheduleAId,
		],
	);

	const isRenderSplitProfilePreview = useMemo(
		() =>
			primaryAgentId &&
			splitProfileId &&
			canViewResidualsDetails &&
			showSplitProfilePreview,
		[
			canViewResidualsDetails,
			primaryAgentId,
			showSplitProfilePreview,
			splitProfileId,
		],
	);

	const renderSplitProfileEyeIcon = () => {
		if (
			primaryAgentId &&
			splitProfileId &&
			canViewResidualsDetails &&
			!showSplitProfilePreview
		) {
			return <EYE />;
		}
		if (
			primaryAgentId &&
			splitProfileId &&
			canViewResidualsDetails &&
			showSplitProfilePreview
		) {
			return <EYEHIDE />;
		}
	};

	const renderScheduleAEyeIcon = () => {
		if (
			primaryAgentId &&
			scheduleAId &&
			canViewResidualsDetails &&
			!showScheduleAPreview
		) {
			return <EYE />;
		}
		if (
			primaryAgentId &&
			scheduleAId &&
			canViewResidualsDetails &&
			showScheduleAPreview
		) {
			return <EYEHIDE />;
		}
	};

	const { data: paymentInfo, isFetching: isPaymentInfoFetching } = useQuery(
		["paymentInfo", primaryAgentId],
		() => agentSetupService.getPaymentInfo({ agentId: primaryAgentId }),
		{
			enabled: !!primaryAgentId,
		},
	);

	const { isScheduleARequired, isScheduleAWarnings } = useMemo(() => {
		const currentPartnerType = selectedAgent?.partnerType;
		const currentBusinessUnit = paymentInfo?.business_unit;

		if (
			currentPartnerType !== undefined &&
			currentBusinessUnit !== undefined
		) {
			return {
				isScheduleARequired:
					Array.isArray(scheduleAList) && !scheduleAList?.length
						? false
						: true,
				isScheduleAWarnings: true,
			};
		}

		return {};
	}, [paymentInfo?.business_unit, scheduleAList, selectedAgent?.partnerType]);

	const isSplitProfileRequired = useMemo(
		() =>
			!(
				isUnderwriting ||
				(Array.isArray(noHighRiskSplitProfiles) &&
					!noHighRiskSplitProfiles?.length)
			),
		[isUnderwriting, noHighRiskSplitProfiles],
	);

	useEffect(() => {
		!isUnderwriting &&
			Array.isArray(noHighRiskSplitProfiles) &&
			noHighRiskSplitProfiles?.length === 1 &&
			dispatch(
				change(
					formName,
					`${sectionName}.splitProfile.id`,
					noHighRiskSplitProfiles[0].id,
				),
			);
	}, [
		dispatch,
		formName,
		isScheduleARequired,
		scheduleAList,
		sectionName,
		noHighRiskSplitProfiles,
		isUnderwriting,
	]);

	const scheduleAsDoNotMatchTheProcess = useCallback(() => {
		const scheduleAWithSpecificProcessor = scheduleAList?.filter(
			scheduleA => scheduleA?.processor_id === parseInt(processorId),
		);

		const scheduleAWithNotProcessor = scheduleAList?.filter(
			scheduleA => scheduleA?.processor_id === null,
		);

		return (
			fp.isEmpty(scheduleAWithSpecificProcessor) &&
			fp.isEmpty(scheduleAWithNotProcessor) &&
			!isEmpty(processorId) &&
			!isLoadingScheduleAList
		);
	}, [isLoadingScheduleAList, processorId, scheduleAList]);

	const ScheduleAWarningMessage = useCallback(() => {
		if (!isScheduleAWarnings) {
			return null;
		}

		if (scheduleAsDoNotMatchTheProcess()) {
			return (
				<small className="text-warning">
					No Schedule A available on your profile for specified
					processor. You can continue with the application, but please
					contact Agent Relations to set one up to avoid any delay to
					residual payments.
				</small>
			);
		} else if (Array.isArray(scheduleAList) && !scheduleAList?.length) {
			return (
				<small className="text-warning">
					No Schedule A available on your profile. You can continue
					with the application
					{isInsideSalesHouseAccountType
						? "."
						: ", but please contact Agent Relations to set one up to avoid any delay to residual payments."}
				</small>
			);
		}

		return null;
	}, [
		isInsideSalesHouseAccountType,
		isScheduleAWarnings,
		scheduleAList,
		scheduleAsDoNotMatchTheProcess,
	]);

	const SplitProfileWarningMassage = useCallback(() => {
		if (isOnlyHighRiskSplitProfileAvailable) {
			return (
				<small className="text-warning">
					Only high risk split profiles available on your profile. If
					the deal is marked high risk by underwriting, they will
					assign the high risk profile. In case the deal is not high
					risk, Agent Relations will be in touch to arrange to set up
					a new split profile.
				</small>
			);
		}

		if (
			Array.isArray(noHighRiskSplitProfiles) &&
			!noHighRiskSplitProfiles?.length
		) {
			return (
				<small className="text-warning">
					No Residual Split profile available on your profile. You can
					continue with the application, but please contact Agent
					Relations to set one up to avoid any delay to residual
					payments.
				</small>
			);
		}

		return null;
	}, [isOnlyHighRiskSplitProfileAvailable, noHighRiskSplitProfiles]);

	return (
		<>
			<div className={styles.formSectionTitle}>
				<h3>
					Choose Your "Schedule A" and "Residuals Split Profile"
					&nbsp;
					{isEmptyPrimaryAgentIdProcessorId && (
						<span className="text-warning">
							(please select Primary Agent and Processor first)
						</span>
					)}
				</h3>
			</div>

			<Row>
				<Col md={12}>
					<Field
						component={CustomDropdownById}
						label="Schedule A"
						validate={isScheduleARequired ? required : undefined}
						hideRequiredMark={!isScheduleARequired}
						name="scheduleA.id"
						smLabel={3}
						labelStyle={{ flex: "0 0 21%", maxWidth: "21%" }}
						smSelect={9}
						selectStyle={{ flex: "0 0 79%", maxWidth: "79%" }}
						loading={
							isLoadingScheduleAList || isPaymentInfoFetching
						}
						items={scheduleAList}
						disabled={isEmptyPrimaryAgentIdProcessorId}
						icon={renderScheduleAEyeIcon()}
						onIconClick={handleOnScheduleAIconClick}
						useFirstAsDefault={
							isScheduleARequired &&
							Array.isArray(scheduleAList) &&
							scheduleAList?.length === 1
						}
					/>
					<ScheduleAWarningMessage />
				</Col>
				<Col md={12}>
					<Field
						component={CustomDropdownById}
						label="Residuals Split Profile"
						validate={isSplitProfileRequired ? required : undefined}
						hideRequiredMark={!isSplitProfileRequired}
						name="splitProfile.id"
						smLabel={3}
						labelStyle={{ flex: "0 0 21%", maxWidth: "21%" }}
						smSelect={9}
						selectStyle={{ flex: "0 0 79%", maxWidth: "79%" }}
						loading={isLoadingSplitProfiles}
						items={noHighRiskSplitProfiles}
						disabled={isEmptyPrimaryAgentIdProcessorId}
						icon={renderSplitProfileEyeIcon()}
						onIconClick={handleOnSplitProfileIconClick}
						useFirstAsDefault={
							!isUnderwriting &&
							Array.isArray(noHighRiskSplitProfiles) &&
							noHighRiskSplitProfiles?.length === 1
						}
					/>
					<SplitProfileWarningMassage />
				</Col>
			</Row>
			{isRenderSplitProfilePreview && (
				<ViewSplitProfile
					agentId={primaryAgentId}
					id={splitProfileId}
				/>
			)}
			{isRenderScheduleAPreview && (
				<ScheduleAView
					userId={primaryAgentId}
					scheduleId={scheduleAId}
				/>
			)}
		</>
	);
};

export default ResidualsFields;
